<script setup lang="ts">
import useUiFormElement from '@mop/ui/composables/useUiFormElement';

defineOptions({
  name: 'UiFormCheckbox',
  inheritAttrs: false,
});

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  name: {
    type: String,
    default: '',
  },
  id: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  boldLabel: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },

  rules: {
    type: Array,
    default: null,
  },
});

const emit = defineEmits(['update:modelValue']);

const { class: className, ...attributes } = useAttrs();
const { validationErrors, register, validate } = useUiFormElement(props);
defineExpose({ validate });

async function handleChange(event: Event) {
  emit('update:modelValue', (event.target as HTMLInputElement).checked);
  await nextTick();
  await validate();
}

onBeforeMount(() => {
  register(getCurrentInstance());
});
</script>

<template>
  <div :class="`form-checkbox ${className}`">
    <div class="form-checkbox__row">
      <label class="form-checkbox__label-wrapper" :for="id || name">
        <input
          :id="id || name"
          :name="name"
          v-bind="attributes"
          :value="modelValue"
          class="form-checkbox__input"
          type="checkbox"
          :disabled="disabled"
          :checked="modelValue === true"
          @change="handleChange"
        />
        <span class="radio-checkmark" />
        <span
          :class="[
            'form-checkbox__label',
            {
              'form-checkbox__label--bold': boldLabel,
            },
          ]"
        >
          <template v-if="label">
            {{ label }}
          </template>
          <template v-else>
            <slot name="label" />
          </template>
          {{ required ? '*' : '' }}
        </span>
      </label>
    </div>
    <div v-if="validationErrors.length" class="form-error form-error--checkbox">
      {{ $mopI18n.t(validationErrors[0]) }}
    </div>
  </div>
</template>
